/**
 * PATH: packages/email-hub/src/feature/components/workbook/ComposeWorkbookModal.tsx
 * FILE_ID: EMAILHUB-WORKBOOK-MODAL-001
 * PURPOSE: The composer's spreadsheet builder — the SHARED platform grid
 *          (@quotech/quote-builder/grid: SheetGrid + A1 formula engine +
 *          sheet tabs, same stack as the kanban Workbook) plus a cell fill
 *          palette, outputting either a real .xlsx attachment (fills +
 *          formulas preserved) or an inline HTML table in the email body.
 * WHY: FEAT-2026-07-03-COMPOSE-SPREADSHEET — "no options for me to scaffold
 *      tables in excel sheet like format (see kanban, reuse the component
 *      concepts don't develop from scratch) … with color and so on and
 *      attach it". Reuses the extracted grid stack rather than forking it.
 * FLOW:
 *   open → edit cells/formulas (A1 engine) → paint fills (palette) →
 *   "Attach .xlsx" → workbookToXlsxFile → onAttach(File) → composer addFiles
 *   "Insert into email" → sheetToHtmlTable → onInsertHtml → TipTap insertHtml
 * DEPENDENCIES:
 *   - @quotech/quote-builder/grid: SheetGrid, formula bar, A1 engine, tabs
 *   - ./composeWorkbookExport: fills palette + xlsx/HTML output (pure)
 * RELATED:
 *   - packages/ui/src/kanban/components/KanbanCardWorkbookModal.tsx: the
 *     kanban sibling this mirrors (that one persists to the card; this one
 *     is a scratch builder whose output IS the attachment/body content).
 */
import { useCallback, useMemo, useState } from 'react'
import { BarChart3, FileSpreadsheet, Loader2, Paperclip, Plus, TableProperties, X } from 'lucide-react'

import {
  SheetGrid,
  SheetGridFormulaBar,
  computeA1Workbook,
  indexToCol,
  makeNewSheet,
  normalizeA1MultiSheet,
  type A1MultiSheetWorkbook,
  type SheetGridColumn,
  type SheetGridRow,
} from '@quotech/quote-builder/grid'

import {
  WORKBOOK_FILLS,
  sheetToHtmlTable,
  workbookToXlsxFile,
  type WorkbookFillMap,
} from './composeWorkbookExport'
import { ComposeChartModal } from '../charts/ComposeChartModal'
import { sheetToChartData } from '../charts/chartSpec'

export interface ComposeWorkbookModalProps {
  /** Seed for the attachment filename (usually the email subject). */
  defaultName?: string
  onAttach: (file: File) => void
  onInsertHtml: (html: string) => void
  onClose: () => void
  /** Error/success surface for the nested chart modal's PNG export —
   *  without it a failed export is SILENT (button stops spinning,
   *  nothing happens). Same signature as ComposeChartModal's. */
  notify?: (message: string, kind: 'success' | 'error') => void
}

const MAX_ROWS = 200
const MAX_COLS = 52

/** Palette token → grid cell classes (static literals so Tailwind ships them). */
const FILL_CLASSES: Record<string, string> = {
  yellow: 'bg-yellow-200 text-yellow-900',
  green: 'bg-green-200 text-green-900',
  blue: 'bg-blue-200 text-blue-900',
  red: 'bg-red-200 text-red-900',
  orange: 'bg-orange-200 text-orange-900',
  purple: 'bg-purple-200 text-purple-900',
  gray: 'bg-gray-200 text-gray-900',
}

export function ComposeWorkbookModal({
  defaultName,
  onAttach,
  onInsertHtml,
  onClose,
  notify,
}: ComposeWorkbookModalProps) {
  const [wb, setWb] = useState<A1MultiSheetWorkbook>(() => normalizeA1MultiSheet(null))
  const [fills, setFills] = useState<WorkbookFillMap>({})
  const [selected, setSelected] = useState('A1')
  const [name, setName] = useState(defaultName?.trim() || 'Spreadsheet')
  const [chartOpen, setChartOpen] = useState(false)

  const sheet = wb.sheets.find((s) => s.id === wb.activeSheetId) ?? wb.sheets[0]
  const sheetFills = fills[sheet.id] ?? {}

  const applySheetEdit = useCallback(
    (updater: (s: A1MultiSheetWorkbook['sheets'][number]) => A1MultiSheetWorkbook['sheets'][number]) => {
      setWb((d) => ({
        ...d,
        sheets: d.sheets.map((s) => (s.id === d.activeSheetId ? updater(s) : s)),
      }))
    },
    [],
  )

  const setCells = useCallback(
    (entries: Array<[string, string]>) => {
      if (entries.length === 0) return
      applySheetEdit((s) => {
        const cells = { ...s.cells }
        for (const [key, raw] of entries) {
          if (raw.trim() === '') delete cells[key]
          else cells[key] = raw
        }
        return { ...s, cells }
      })
    },
    [applySheetEdit],
  )

  const setCell = useCallback(
    (key: string, raw: string) => setCells([[key, raw]]),
    [setCells],
  )

  const paintFill = useCallback(
    (token: string | null) => {
      setWb((current) => {
        const activeId = current.activeSheetId
        setFills((prev) => {
          const forSheet = { ...(prev[activeId] ?? {}) }
          if (token === null) delete forSheet[selected]
          else forSheet[selected] = token
          return { ...prev, [activeId]: forSheet }
        })
        return current
      })
    },
    [selected],
  )

  const addSheet = useCallback(() => {
    setWb((d) => {
      const next = makeNewSheet(d.sheets)
      if (!next) return d
      return { sheets: [...d.sheets, next], activeSheetId: next.id }
    })
    setSelected('A1')
  }, [])

  const switchSheet = useCallback((id: string) => {
    setWb((prev) => (prev.activeSheetId === id ? prev : { ...prev, activeSheetId: id }))
    setSelected('A1')
  }, [])

  const computed = useMemo(() => computeA1Workbook(sheet.cells), [sheet.cells])
  const colLetters = useMemo(
    () => Array.from({ length: sheet.cols }, (_, c) => indexToCol(c)),
    [sheet.cols],
  )
  const gridColumns = useMemo<SheetGridColumn[]>(
    () => colLetters.map((letter) => ({ field: letter, headerName: letter, width: 110 })),
    [colLetters],
  )
  const gridRows = useMemo<SheetGridRow[]>(() => {
    const rows: SheetGridRow[] = []
    for (let r = 1; r <= sheet.rows; r++) {
      const row: SheetGridRow = { id: String(r) }
      for (const letter of colLetters) row[letter] = sheet.cells[`${letter}${r}`] ?? ''
      rows.push(row)
    }
    return rows
  }, [colLetters, sheet.cells, sheet.rows])

  const formatCellDisplay = useCallback(
    (row: SheetGridRow, field: string) => computed.values[`${field}${row.id}`] ?? '',
    [computed.values],
  )
  const getCellClass = useCallback(
    (row: SheetGridRow, field: string) => {
      const ref = `${field}${row.id}`
      if (computed.errors[ref]) return 'text-red-600 dark:text-red-400'
      const token = sheetFills[ref]
      return token ? FILL_CLASSES[token] : undefined
    },
    [computed.errors, sheetFills],
  )
  const handlePasteRange = useCallback(
    (startRowId: string, startField: string, cells: string[][]) => {
      const startRow = Number(startRowId)
      const startCol = colLetters.indexOf(startField)
      if (!Number.isFinite(startRow) || startCol < 0) return
      const entries: Array<[string, string]> = []
      cells.forEach((line, dr) => {
        const r = startRow + dr
        if (r > MAX_ROWS) return
        line.forEach((value, dc) => {
          const c = startCol + dc
          if (c >= MAX_COLS) return
          entries.push([`${indexToCol(c)}${r}`, value])
        })
      })
      setCells(entries)
    },
    [colLetters, setCells],
  )
  const handleFillDown = useCallback(
    (targetRowIds: string[], fields: string[], sourceRowId: string) => {
      const entries: Array<[string, string]> = []
      for (const field of fields) {
        const source = sheet.cells[`${field}${sourceRowId}`] ?? ''
        for (const rowId of targetRowIds) entries.push([`${field}${rowId}`, source])
      }
      setCells(entries)
    },
    [sheet.cells, setCells],
  )

  const handleAttach = useCallback(() => {
    onAttach(workbookToXlsxFile(wb, fills, name))
    onClose()
  }, [wb, fills, name, onAttach, onClose])

  const handleInsert = useCallback(() => {
    const html = sheetToHtmlTable(sheet, sheetFills)
    if (html === '') return
    onInsertHtml(html)
    onClose()
  }, [sheet, sheetFills, onInsertHtml, onClose])

  const selRaw = sheet.cells[selected] ?? ''
  const hasContent = wb.sheets.some((s) =>
    Object.values(s.cells).some((v) => (v ?? '').trim() !== ''),
  )
  // "Graph creation in the excel-like builder": row 1 = series headers,
  // column A = labels. Null when the sheet doesn't fit that shape.
  const chartSeed = useMemo(
    () =>
      sheetToChartData({
        cells: sheet.cells,
        values: computed.values,
        rows: sheet.rows,
        cols: sheet.cols,
      }),
    [sheet.cells, sheet.rows, sheet.cols, computed.values],
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label="Spreadsheet builder"
      data-testid="compose-workbook-modal"
    >
      <div className="flex h-[85vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-background shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2.5">
          <FileSpreadsheet className="h-4 w-4 text-emerald-600" />
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Spreadsheet name"
            className="min-w-0 flex-1 rounded border border-transparent bg-transparent px-2 py-1 text-sm font-medium text-foreground hover:border-border focus:border-primary focus:outline-none"
          />
          {/* Fill palette — paints the selected cell */}
          <div className="flex items-center gap-1" aria-label="Cell fill colours">
            {Object.entries(WORKBOOK_FILLS).map(([token, f]) => (
              <button
                key={token}
                type="button"
                title={`Fill ${selected} ${f.label.toLowerCase()}`}
                aria-label={`Fill ${selected} ${f.label.toLowerCase()}`}
                onClick={() => paintFill(token)}
                className={`h-5 w-5 rounded border ${sheetFills[selected] === token ? 'border-foreground ring-1 ring-foreground' : 'border-border'}`}
                style={{ backgroundColor: f.hex }}
              />
            ))}
            <button
              type="button"
              title={`Clear fill on ${selected}`}
              aria-label={`Clear fill on ${selected}`}
              onClick={() => paintFill(null)}
              className="flex h-5 w-5 items-center justify-center rounded border border-border text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
          <button
            type="button"
            onClick={() => applySheetEdit((s) => ({ ...s, rows: Math.min(MAX_ROWS, s.rows + 6) }))}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
          >
            <Plus className="h-3 w-3" /> Rows
          </button>
          <button
            type="button"
            onClick={() => applySheetEdit((s) => ({ ...s, cols: Math.min(MAX_COLS, s.cols + 2) }))}
            className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-xs text-muted-foreground hover:bg-accent"
          >
            <Plus className="h-3 w-3" /> Cols
          </button>
          <button
            type="button"
            aria-label="Close spreadsheet builder"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Formula bar */}
        <div className="shrink-0 border-b border-border px-2 py-1.5">
          <SheetGridFormulaBar
            cellLabel={selected}
            value={selRaw}
            onChangeValue={(v) => setCell(selected, v)}
            hint="Type = then click cells · =SUM(A1:A3) · paste from Excel · Ctrl/⌘+D fills down"
          />
        </div>

        {/* Grid */}
        <div className="min-h-0 flex-1">
          <SheetGrid
            key={sheet.id}
            columns={gridColumns}
            rows={gridRows}
            onChangeCell={(rowId, field, value) => setCell(`${field}${rowId}`, value)}
            onPasteRange={handlePasteRange}
            onFillDown={handleFillDown}
            onFocusChange={(row, field) => { if (row && field) setSelected(`${field}${row.id}`) }}
            formatCellDisplay={formatCellDisplay}
            getCellClass={getCellClass}
            getCellA1Ref={(row, field) => `${field}${row.id}`}
            loadingFallback={
              <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading grid…
              </div>
            }
          />
        </div>

        {/* Sheet tabs + actions */}
        <div className="flex shrink-0 items-center gap-0.5 border-t border-border bg-muted/40 px-2 py-1.5">
          {wb.sheets.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => switchSheet(s.id)}
              className={`rounded-t border px-2 py-0.5 text-xs ${
                s.id === wb.activeSheetId
                  ? 'border-border bg-background font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:bg-accent'
              }`}
            >
              {s.name}
            </button>
          ))}
          <button
            type="button"
            aria-label="Add sheet"
            onClick={addSheet}
            className="ml-1 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <Plus className="h-3 w-3" /> Sheet
          </button>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setChartOpen(true)}
            disabled={!chartSeed}
            title={
              chartSeed
                ? 'Chart this sheet (row 1 = series names, column A = labels)'
                : 'To chart this sheet, put series names in row 1 (B1…) and labels in column A (A2…)'
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            <BarChart3 className="h-3.5 w-3.5" /> Chart
          </button>
          <button
            type="button"
            onClick={handleInsert}
            disabled={!hasContent}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground hover:bg-accent disabled:opacity-50"
          >
            <TableProperties className="h-3.5 w-3.5" /> Insert into email
          </button>
          <button
            type="button"
            onClick={handleAttach}
            disabled={!hasContent}
            className="ml-2 inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            <Paperclip className="h-3.5 w-3.5" /> Attach .xlsx
          </button>
        </div>
      </div>

      {chartOpen && chartSeed && (
        <ComposeChartModal
          defaultName={sheet.name === 'Sheet 1' ? name : sheet.name}
          initialChart={chartSeed}
          onAttach={onAttach}
          onInsertHtml={onInsertHtml}
          onClose={() => setChartOpen(false)}
          notify={notify}
        />
      )}
    </div>
  )
}
